import { getImageProps } from "next/image";
import type { Image as GalleryImage } from "@/types";

// Photos from the owners' own wedding album, published here with their
// permission. Copies live in public/ so the page never depends on an event's
// storage; thumbs/ holds square crops centred on the faces.
const DEMO_MEDIA = "/photowall-demo";

export interface DemoPhoto {
  id: number;
  alt: string;
  /** Position in the wedding day, used by the Timeline order. */
  takenOrder: number;
  aspectRatio: number;
  /** CSS object-position that keeps faces in frame when a tile crops the photo. */
  focus: string;
  /** Sized for a wall tile. */
  url: string;
  /** A square crop around the faces, for arrival cards and phone thumbnails. */
  thumbnailUrl: string;
}

interface DemoPhotoSource {
  file: string;
  alt: string;
  width: number;
  height: number;
  /** Centre of the faces, in percent of width and height. */
  focus: [number, number];
}

// In the order of the day: getting ready, church, portraits, reception, dinner, party.
const DEMO_PHOTO_SOURCES: DemoPhotoSource[] = [
  { file: "01-getting-ready.webp", alt: "The bride having her hair done before the wedding", width: 1200, height: 1600, focus: [39, 36] },
  { file: "02-bride.webp", alt: "The bride in her lace dress, in profile", width: 1600, height: 1169, focus: [32, 37] },
  { file: "03-groom.webp", alt: "The groom looking out through a doorway", width: 1600, height: 1085, focus: [41, 42] },
  { file: "04-arrival.webp", alt: "The bride stepping out of a vintage car under an umbrella", width: 1030, height: 1600, focus: [48, 35] },
  { file: "05-church.webp", alt: "The bride and groom seated at the altar during the ceremony", width: 1600, height: 1059, focus: [52, 19] },
  { file: "06-flags.webp", alt: "Guests waving Danish flags and blowing bubbles as the couple walks out", width: 1600, height: 1122, focus: [50, 46] },
  { file: "07-umbrella-kiss.webp", alt: "The couple kissing under a clear umbrella in the rain", width: 1600, height: 1173, focus: [52, 40] },
  { file: "08-bride-umbrella.webp", alt: "The bride holding her bouquet under a clear umbrella", width: 1049, height: 1600, focus: [53, 37] },
  { file: "09-bridge.webp", alt: "The couple under a clear umbrella on a wooden bridge", width: 1078, height: 1600, focus: [52, 32] },
  { file: "10-couple.webp", alt: "The groom kissing the bride on the head", width: 1081, height: 1600, focus: [63, 33] },
  { file: "11-car.webp", alt: "The couple smiling in the back seat of the wedding car", width: 1600, height: 1067, focus: [47, 33] },
  { file: "12-friends.webp", alt: "The bride with two friends in the garden", width: 1099, height: 1600, focus: [49, 25] },
  { file: "13-group.webp", alt: "The bride with a group of guests outside the venue", width: 1600, height: 1037, focus: [50, 47] },
  { file: "14-drinks.webp", alt: "Three guests smiling with drinks at the reception", width: 1600, height: 1067, focus: [54, 31] },
  { file: "15-cheers.webp", alt: "A guest raising two glasses at the reception", width: 1600, height: 1085, focus: [35, 44] },
  { file: "16-laughter.webp", alt: "A guest laughing with a glass of champagne", width: 1600, height: 1142, focus: [36, 27] },
  { file: "17-caricature.webp", alt: "Two guests holding up a caricature drawing", width: 1600, height: 1157, focus: [51, 32] },
  { file: "18-table.webp", alt: "Flowers and candles on a dinner table", width: 1600, height: 1267, focus: [63, 74] },
  { file: "19-dinner-hall.webp", alt: "Guests around a dinner table in the reception hall", width: 1600, height: 1200, focus: [50, 57] },
  { file: "20-dinner-couple.webp", alt: "The bride and groom at their dinner table", width: 1600, height: 1200, focus: [48, 38] },
  { file: "21-dinner-guests.webp", alt: "Guests chatting at dinner", width: 1600, height: 1258, focus: [38, 37] },
  { file: "22-cake.webp", alt: "The bride feeding the groom a bite of wedding cake", width: 900, height: 1600, focus: [56, 41] },
  { file: "23-dancing.webp", alt: "The bride dancing at the reception", width: 1200, height: 1600, focus: [63, 33] },
  { file: "24-just-married.webp", alt: "The wedding car with a just married sign", width: 900, height: 1600, focus: [52, 50] },
];

const THUMBNAIL_SIZE = 480;

function getOptimizedUrl(src: string, width: number, height: number, displayWidth: number): string {
  // getImageProps hands back its 2x candidate as `src`, so ask for half.
  const halfWidth = displayWidth / 2;
  const { props } = getImageProps({
    src,
    alt: "",
    width: halfWidth,
    height: Math.round((halfWidth * height) / width),
  });
  return props.src;
}

export const DEMO_PHOTOS: DemoPhoto[] = DEMO_PHOTO_SOURCES.map((source, index) => ({
  id: index + 1,
  alt: source.alt,
  takenOrder: index,
  aspectRatio: source.width / source.height,
  focus: `${source.focus[0]}% ${source.focus[1]}%`,
  url: getOptimizedUrl(
    `${DEMO_MEDIA}/${source.file}`,
    source.width,
    source.height,
    source.width > source.height ? 1080 : 828
  ),
  thumbnailUrl: getOptimizedUrl(`${DEMO_MEDIA}/thumbs/${source.file}`, THUMBNAIL_SIZE, THUMBNAIL_SIZE, 384),
}));

/** The flowers on the dinner table, the album's own cover photo. */
export const DEMO_COVER_PHOTO = DEMO_PHOTOS[17];

const photoLoads = new Map<string, Promise<void>>();

/** Resolves once a photo is decoded (or failed), so a layout never shows half-loaded. */
export function loadDemoPhoto(url: string): Promise<void> {
  let load = photoLoads.get(url);
  if (!load) {
    const image = new window.Image();
    image.src = url;
    load = image.decode().catch(() => undefined);
    photoLoads.set(url, load);
  }
  return load;
}

/** Warms every demo photo, as the live wall loads photos before showing them. */
export function preloadDemoPhotos(): void {
  DEMO_PHOTOS.forEach((photo) => {
    void loadDemoPhoto(photo.url);
    void loadDemoPhoto(photo.thumbnailUrl);
  });
}

/** A gallery row for the arrival engine, the same shape the real wall reads. */
export function createDemoGalleryImage(id: number, photo: DemoPhoto): GalleryImage {
  const createdAt = new Date("2026-06-20T16:00:00Z");
  return {
    id,
    created_at: createdAt,
    last_modified_date: createdAt,
    ownerUserId: "photowall-demo",
    ownerEventId: -1,
    size: 0,
    downloadCount: 0,
    type: "image",
    uploaded: true,
    deleted: false,
    status: "OPTIMIZED",
    zipped: false,
    photographer: false,
    s3URL: photo.url,
    optimized_url: photo.url,
    thumbnail_url: photo.thumbnailUrl,
    aspectRatio: photo.aspectRatio,
  };
}
